import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LeadStatus } from '../../types/lead';
import Button from './Button';

interface BulkActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyToSelected: (status: LeadStatus) => void;
  onApplyToAll: (status: LeadStatus) => void;
  selectedCount: number;
  totalCount: number;
  availableStatuses: LeadStatus[];
  isLoading?: boolean;
}

const BulkActionModal: React.FC<BulkActionModalProps> = ({
  isOpen,
  onClose,
  onApplyToSelected,
  onApplyToAll,
  selectedCount,
  totalCount,
  availableStatuses,
  isLoading = false
}) => {
  const [selectedStatus, setSelectedStatus] = useState<LeadStatus>('new');

  if (!isOpen) return null;

  const handleApplyToSelected = () => {
    onApplyToSelected(selectedStatus);
  };

  const handleApplyToAll = () => {
    onApplyToAll(selectedStatus);
  };

  const getStatusDisplayName = (status: LeadStatus): string => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                Bulk Status Update
              </h3>
              
              <div className="mt-4">
                <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select New Status
                </label>
                <select
                  id="status-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as LeadStatus)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  disabled={isLoading}
                >
                  {availableStatuses.map((status) => (
                    <option key={status} value={status}>
                      {getStatusDisplayName(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-6 space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Apply to Selected Leads
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Update status for {selectedCount} selected {selectedCount === 1 ? 'lead' : 'leads'}
                  </p>
                  <Button
                    onClick={handleApplyToSelected}
                    disabled={selectedCount === 0 || isLoading}
                    variant="primary"
                    size="sm"
                    className="w-full"
                  >
                    {isLoading ? 'Applying...' : `Apply to Selected (${selectedCount})`}
                  </Button>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-orange-900 mb-2">
                    Apply to All Results
                  </h4>
                  <p className="text-sm text-orange-700 mb-3">
                    Update status for all {totalCount} {totalCount === 1 ? 'lead' : 'leads'} matching current filters
                  </p>
                  <Button
                    onClick={handleApplyToAll}
                    disabled={totalCount === 0 || isLoading}
                    variant="outlined"
                    size="sm"
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                  >
                    {isLoading ? 'Applying...' : `Apply to All Results (${totalCount})`}
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  size="sm"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;